import { ComponentPropsWithRef, useState } from "react"
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons"

import { Input } from "@/components/chadcn/Input"
import { Textarea } from "@/components/chadcn/TextArea"
import cn from "@/util/cn"

type EmailInputProps = Omit<ComponentPropsWithRef<"input">, "autoComplete"> & {
  isError?: boolean
  autoComplete?: "email" | "username"
}

export const EmailInput = ({
  autoComplete = "email",
  className,
  isError,
  ...otherProps
}: EmailInputProps) => (
  <Input
    className={cn(
      isError ? "ring-red-600 focus-visible:ring-red-600" : "",
      className
    )}
    type="email"
    autoComplete={autoComplete}
    {...otherProps}
  />
)

type PasswordInputProps = {
  autoComplete?: "current-password" | "new-password"
  isError?: boolean
} & Omit<ComponentPropsWithRef<"input">, "autoComplete">

export const PasswordInput = ({
  isError,
  className,
  ...otherProps
}: PasswordInputProps) => {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <Input
        className={cn(
          "pr-10",
          isError ? "ring-red-600 focus-visible:ring-red-600" : "",
          className
        )}
        type={show ? "text" : "password"}
        {...otherProps}
      />
      <button
        type="button"
        onClick={() => setShow((current) => !current)}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-primary-contrast/60 hover:text-primary-contrast"
      >
        {show ? <EyeClosedIcon /> : <EyeOpenIcon />}
      </button>
    </div>
  )
}

type SelectInputProps<T extends string> = {
  options: { value: T; title: string; hidden?: boolean; disabled?: boolean }[]
  placeholder?: string
} & Omit<ComponentPropsWithRef<"select">, "children">

export const SelectInput = <T extends string>({
  options = [],
  className,
  ...otherProps
}: SelectInputProps<T>) => (
  <select
    className={cn(
      "block w-full rounded-md border-0 px-3 py-2 text-base leading-6 ring-1 ring-inset ring-input placeholder:text-primary-contrast/60 focus:ring-2 focus:ring-inset focus:ring-primary",
      "disabled:cursor-not-allowed disabled:text-primary-contrast/60",
      className
    )}
    {...otherProps}
  >
    {options.map(({ value, title, hidden, disabled }, i) => (
      <option key={`option-${i}`} value={value} hidden={hidden} disabled={disabled}>
        {title}
      </option>
    ))}
  </select>
)

type TextInputProps = ComponentPropsWithRef<"input"> & {
  isError?: boolean
}

export const TextInput = ({
  className,
  isError,
  ...otherProps
}: TextInputProps) => (
  <Input
    className={cn(
      isError ? "ring-red-600 focus-visible:ring-red-600" : "",
      className
    )}
    type="text"
    {...otherProps}
  />
)

type TextareaInputProps = ComponentPropsWithRef<"textarea"> & {
  isError?: boolean
}

export const TextareaInput = ({
  className,
  isError,
  ...otherProps
}: TextareaInputProps) => (
  <Textarea
    className={cn(
      isError ? "ring-error focus:ring-error" : "",
      className
    )}
    {...otherProps}
  />
)

type PhoneInputProps = ComponentPropsWithRef<"input"> & {
  isError?: boolean
}

export const PhoneInput = ({
  className,
  isError,
  ...otherProps
}: PhoneInputProps) => (
  <Input
    className={cn(
      isError ? "ring-red-600 focus-visible:ring-red-600" : "",
      className
    )}
    type="text"
    {...otherProps}
  />
)

type DateInputProps = ComponentPropsWithRef<"input"> & {
  isError?: boolean
}

export const DateInput = ({
  className,
  isError,
  ...otherProps
}: DateInputProps) => (
  <Input
    className={cn(
      isError ? "ring-red-600 focus-visible:ring-red-600" : "",
      className
    )}
    type="date"
    {...otherProps}
  />
)

type CheckboxInputProps = Omit<ComponentPropsWithRef<"input">, "type">

export const CheckboxInput = ({ className, ...props }: CheckboxInputProps) => (
  <input
    className={cn(
      "h-5 w-5 rounded-md border-input text-primary focus:ring-2 focus:ring-primary/40 focus:ring-offset-0",
      className
    )}
    type="checkbox"
    {...props}
  />
)
